/**
 * Superannuation Caps and Limits for 2025/26 Financial Year
 * Updated Transfer Balance Cap to $2,000,000
 */

export interface SuperannuationCaps2025 {
  concessionalContributionCap: number;
  nonConcessionalContributionCap: number;
  bringForwardCap: number;
  transferBalanceCap: number;
  cgtLifetimeExemptionCap: number;
  totalSuperBalanceThresholds: {
    nonConcessionalCapNil: number;
    bringForwardRestricted: number;
    bringForwardProhibited: number;
    carryForwardRestricted: number;
  };
  downsizer: {
    minimumAge: number;
    maximumContribution: number;
    eligibilityRequirements: string[];
  };
  workTest: {
    age: number;
    requirements: string[];
  };
}

export interface ContributionCapAssessment {
  concessionalCap: number;
  nonConcessionalCap: number;
  bringForwardAvailable: boolean;
  bringForwardCap: number;
  carryForwardAvailable: boolean;
  availableCarryForward: number;
  totalSuperBalance: number;
  warnings: string[];
  recommendations: string[];
}

export interface TransferBalanceCapAssessment {
  currentTransferBalanceCap: number;
  personalTransferBalanceCap: number;
  spaceAvailable: number;
  isNewPensioner: boolean;
  proportionalIndexation: number;
  canCommenceNewPension: boolean;
  warnings: string[];
}

export class SuperannuationCaps2025 {
  private static readonly CAPS_2025_26: SuperannuationCaps2025 = {
    concessionalContributionCap: 30000,
    nonConcessionalContributionCap: 120000,
    bringForwardCap: 360000, // 3 years x $120,000
    transferBalanceCap: 2000000, // Increased from $1,900,000
    cgtLifetimeExemptionCap: 1865000, // Increased by $85,000
    totalSuperBalanceThresholds: {
      nonConcessionalCapNil: 2000000,
      bringForwardRestricted: 1760000, // $2M - $240K
      bringForwardProhibited: 1880000, // $2M - $120K
      carryForwardRestricted: 500000
    },
    downsizer: {
      minimumAge: 55, // Reduced from 60
      maximumContribution: 300000,
      eligibilityRequirements: [
        "Home must be owned for 10+ years",
        "Home must be main residence or former main residence",
        "Sale proceeds of at least $300,000",
        "Contribution made within 90 days of settlement"
      ]
    },
    workTest: {
      age: 67,
      requirements: [
        "Must work at least 40 hours in 30 consecutive days",
        "Work must be paid employment or self-employment",
        "Required for voluntary contributions after age 67"
      ]
    }
  };

  /**
   * Assess contribution caps and available space for 2025/26
   */
  static assessContributionCaps(
    totalSuperBalance: number,
    age: number,
    previousConcessionalContributions: number[] = [], // Last 5 years
    hasValidCarryForward: boolean = true
  ): ContributionCapAssessment {
    const caps = this.CAPS_2025_26;
    
    // Concessional cap
    const concessionalCap = caps.concessionalContributionCap;
    
    // Non-concessional cap based on TSB
    let nonConcessionalCap = caps.nonConcessionalContributionCap;
    if (totalSuperBalance >= caps.totalSuperBalanceThresholds.nonConcessionalCapNil) {
      nonConcessionalCap = 0;
    }
    
    // Bring-forward availability
    let bringForwardAvailable = false;
    let bringForwardCap = 0;
    
    if (totalSuperBalance < caps.totalSuperBalanceThresholds.bringForwardProhibited) {
      bringForwardAvailable = true;
      
      if (totalSuperBalance < caps.totalSuperBalanceThresholds.bringForwardRestricted) {
        // Full 3-year bring-forward available
        bringForwardCap = caps.bringForwardCap;
      } else {
        // Restricted to 2 years
        bringForwardCap = caps.nonConcessionalContributionCap * 2;
      }
    }
    
    // Carry-forward assessment
    const carryForwardAvailable = hasValidCarryForward && 
      totalSuperBalance < caps.totalSuperBalanceThresholds.carryForwardRestricted;
    
    const availableCarryForward = carryForwardAvailable 
      ? this.calculateAvailableCarryForward(previousConcessionalContributions, concessionalCap)
      : 0;
    
    // Generate warnings and recommendations
    const warnings: string[] = [];
    const recommendations: string[] = [];
    
    if (totalSuperBalance >= caps.totalSuperBalanceThresholds.nonConcessionalCapNil) {
      warnings.push("Total Super Balance exceeds $2M - no non-concessional contributions allowed");
    }
    
    if (!carryForwardAvailable && totalSuperBalance >= caps.totalSuperBalanceThresholds.carryForwardRestricted) {
      warnings.push("Total Super Balance exceeds $500K - carry-forward concessional contributions not available");
    }
    
    if (age >= caps.workTest.age) {
      warnings.push("Work test applies for voluntary contributions");
      recommendations.push("Consider salary sacrifice or mandated employer contributions");
    }
    
    if (age >= caps.downsizer.minimumAge) {
      recommendations.push("Downsizer contributions available from age 55");
    }
    
    return {
      concessionalCap,
      nonConcessionalCap,
      bringForwardAvailable,
      bringForwardCap,
      carryForwardAvailable,
      availableCarryForward,
      totalSuperBalance,
      warnings,
      recommendations
    };
  }

  /**
   * Assess Transfer Balance Cap for 2025/26
   */
  static assessTransferBalanceCap(
    currentPensionBalance: number = 0,
    isNewPensioner: boolean = true,
    previousTransferBalanceCapUsed: number = 0
  ): TransferBalanceCapAssessment {
    const caps = this.CAPS_2025_26;
    
    let personalTransferBalanceCap: number;
    let proportionalIndexation = 0;
    
    if (isNewPensioner) {
      // New pensioners get the full current cap
      personalTransferBalanceCap = caps.transferBalanceCap;
    } else {
      // Existing pensioners get proportional indexation
      const indexationFactor = caps.transferBalanceCap / 1900000; // From previous cap
      proportionalIndexation = (indexationFactor - 1) * previousTransferBalanceCapUsed;
      personalTransferBalanceCap = 1900000 + proportionalIndexation;
    }
    
    const spaceAvailable = Math.max(0, personalTransferBalanceCap - currentPensionBalance);
    const canCommenceNewPension = spaceAvailable > 0;
    
    const warnings: string[] = [];
    
    if (currentPensionBalance >= personalTransferBalanceCap) {
      warnings.push("Transfer Balance Cap exceeded - excess must be commuted");
    }
    
    if (spaceAvailable < 100000) {
      warnings.push("Limited Transfer Balance Cap space remaining");
    }
    
    return {
      currentTransferBalanceCap: caps.transferBalanceCap,
      personalTransferBalanceCap,
      spaceAvailable,
      isNewPensioner,
      proportionalIndexation,
      canCommenceNewPension,
      warnings
    };
  }

  /**
   * Calculate available carry-forward concessional contributions
   */
  private static calculateAvailableCarryForward(
    previousContributions: number[],
    currentCap: number
  ): number {
    // Can carry forward unused concessional cap space from previous 5 years
    let totalCarryForward = 0;
    
    // Calculate for each of the previous 5 years
    for (let i = 0; i < Math.min(5, previousContributions.length); i++) {
      const yearContributions = previousContributions[i] || 0;
      const unusedCap = Math.max(0, currentCap - yearContributions);
      totalCarryForward += unusedCap;
    }
    
    return totalCarryForward;
  }

  /**
   * Validate downsizer contribution eligibility
   */
  static validateDownsizerEligibility(
    age: number,
    homeOwnershipYears: number,
    saleProceeds: number,
    isMainResidence: boolean,
    daysSinceSettlement: number
  ): {
    eligible: boolean;
    maxContribution: number;
    errors: string[];
    requirements: string[];
  } {
    const caps = this.CAPS_2025_26;
    const errors: string[] = [];
    
    if (age < caps.downsizer.minimumAge) {
      errors.push(`Must be at least ${caps.downsizer.minimumAge} years old`);
    }
    
    if (homeOwnershipYears < 10) {
      errors.push("Home must be owned for at least 10 years");
    }
    
    if (saleProceeds < caps.downsizer.maximumContribution) {
      errors.push(`Sale proceeds must be at least $${caps.downsizer.maximumContribution.toLocaleString()}`);
    }
    
    if (!isMainResidence) {
      errors.push("Property must be main residence or former main residence");
    }
    
    if (daysSinceSettlement > 90) {
      errors.push("Contribution must be made within 90 days of settlement");
    }
    
    const eligible = errors.length === 0;
    const maxContribution = eligible ? caps.downsizer.maximumContribution : 0;
    
    return {
      eligible,
      maxContribution,
      errors,
      requirements: caps.downsizer.eligibilityRequirements
    };
  }

  /**
   * Get all superannuation caps for 2025/26
   */
  static getSuperannuationCaps(): SuperannuationCaps2025 {
    return this.CAPS_2025_26;
  }

  /**
   * Calculate optimal contribution strategy
   */
  static calculateOptimalContributionStrategy(
    currentAge: number,
    currentIncome: number,
    totalSuperBalance: number,
    marginalTaxRate: number,
    superTaxRate: number = 0.15,
    availableFunds: number
  ): {
    concessionalRecommendation: number;
    nonConcessionalRecommendation: number;
    carryForwardRecommendation: number;
    taxSavings: number;
    reasoning: string[];
  } {
    const assessment = this.assessContributionCaps(totalSuperBalance, currentAge);
    
    let concessionalRecommendation = 0;
    let nonConcessionalRecommendation = 0;
    let carryForwardRecommendation = 0;
    const reasoning: string[] = [];
    
    // Prioritize concessional contributions for tax savings
    const taxBenefit = marginalTaxRate - superTaxRate;
    
    if (taxBenefit > 0 && assessment.concessionalCap > 0) {
      const maxConcessional = Math.min(
        availableFunds,
        assessment.concessionalCap,
        currentIncome * 0.25 // Reasonable limit
      );
      
      concessionalRecommendation = maxConcessional;
      availableFunds -= maxConcessional;
      reasoning.push(`Concessional contributions provide ${(taxBenefit * 100).toFixed(1)}% tax benefit`);
    }
    
    // Consider carry-forward if available
    if (assessment.carryForwardAvailable && availableFunds > 0) {
      const carryForwardAmount = Math.min(availableFunds, assessment.availableCarryForward);
      carryForwardRecommendation = carryForwardAmount;
      availableFunds -= carryForwardAmount;
      reasoning.push("Utilizing carry-forward concessional cap space");
    }
    
    // Non-concessional contributions for remaining funds
    if (assessment.nonConcessionalCap > 0 && availableFunds > 0) {
      nonConcessionalRecommendation = Math.min(availableFunds, assessment.nonConcessionalCap);
      reasoning.push("Non-concessional contributions for additional super growth");
    }
    
    const taxSavings = (concessionalRecommendation + carryForwardRecommendation) * taxBenefit;
    
    return {
      concessionalRecommendation,
      nonConcessionalRecommendation,
      carryForwardRecommendation,
      taxSavings,
      reasoning
    };
  }
}
